import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

const GENERIC_MSG = "If an account exists for this email, you will receive a password reset link.";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // Even on transient errors we show the same generic message so we
      // never leak whether an account exists. A toast surfaces only real
      // network problems for the user's benefit.
      const code = err?.code;
      if (!err?.response) {
        toast.error("Server unavailable. Please try again shortly.");
      } else if (code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
        toast.error("The server is taking too long to respond. Please try again shortly.");
      } else {
        // For 4xx/5xx we still display the generic success screen because
        // the backend always returns 200 on this route; if we got here it's
        // a client-side validation issue (e.g., invalid email format).
        setSent(true);
      }
    }
    setBusy(false);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold tracking-tight mb-2">Reset your password</h1>
          <p className="text-sm text-zinc-500">
            Enter the email associated with your account and we&apos;ll send you a reset link.
          </p>
        </div>
        <div className="bg-[#12141C] border border-[#1E212B] rounded-xl p-8">
          {sent ? (
            <div data-testid="forgot-success" className="space-y-4">
              <div className="text-sm text-zinc-300 leading-relaxed">
                {GENERIC_MSG}
              </div>
              <div className="text-xs text-zinc-500">
                The link expires in 60 minutes and can only be used once. If you don&apos;t see the email, check your spam folder.
              </div>
              <Link
                to="/login"
                data-testid="forgot-back-to-login"
                className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to log in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">Email</Label>
                <Input
                  id="email"
                  data-testid="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 bg-[#090A0F] border-[#1E212B] focus:border-blue-500 focus-visible:ring-blue-500/30 h-11"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white h-11"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                ) : (
                  "Send reset link"
                )}
              </Button>
              <div className="pt-2 text-center">
                <Link
                  to="/login"
                  data-testid="forgot-back-link"
                  className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-300"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to log in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
